import { ReduxAction } from 'src/AppState/ReduxAction';
import { Amount } from 'src_common/common/amount/Amount';
import { observable, action, computed, makeAutoObservable } from 'mobx';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { Resource, Result } from 'src_common/common/mobx-utils/Resource';
import { OpenapiProxyStaffFreebetCreditsResponse200Type } from 'src/api_openapi/generated/openapi_proxy_staff_freebet_credits';
import { List } from 'immutable';
import { Common } from 'src/AppState/Common';

export class FreeBetsProcedureState {
    @observable public freebetCreditsResource: Resource<OpenapiProxyStaffFreebetCreditsResponse200Type>;
    @observable public showModal: boolean = false;
    @observable public reason: string = '';
    @observable public creditId: number | string | null = null;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly customerId: number,
        private readonly permissionGrants: List<unknown>,
        private readonly reduxAction: ReduxAction,
        private readonly refreshFreebetLists: () => void,
        private readonly common: Common,
        private readonly cancelBonusBonusEngine: (bonusId: string) => Promise<void>
    ) {
        this.freebetCreditsResource = new Resource(
            async (): Promise<OpenapiProxyStaffFreebetCreditsResponse200Type> => {
                const response = await this.sdkStaff.getFreebetCredit({ customer_id: this.customerId });

                if ($appState.configState.config.showBonusEngine === true) {
                    const responseBonusEngine =
                        await this.common.trpcClient.client.bonusCampaignsRouter.getBonusBalance.mutate({
                            accountId: this.customerId,
                        });

                    if (responseBonusEngine.status !== 200) {
                        throw new Error(`responseBonusEngine error: ${responseBonusEngine.body.message}`);
                    }

                    const freeBetFromResponse = responseBonusEngine.body['free-bet'];
                    return {
                        bonusCredits: [],
                        bonusWallet: {
                            accountId: this.customerId,
                            code: '',
                            createdAt: '',
                            id: 0,
                            universe: freeBetFromResponse?.universe ?? '',
                        },
                        totalAmount: freeBetFromResponse?.availableAmount ?? 0,
                    };
                }

                return response;
            }
        );
        makeAutoObservable(this);
    }

    @computed public get freebetCredits(): Result<OpenapiProxyStaffFreebetCreditsResponse200Type> {
        return this.freebetCreditsResource.get();
    }

    @computed public get permisionsAllowed(): boolean {
        if (this.permissionGrants.size > 0) {
            for (let i = 0; i < this.permissionGrants.size; i++) {
                if (this.permissionGrants.getIn([i, 'permission']) === 'wallet/manage-bonuses') {
                    return true;
                }
            }
        }
        return false;
    }

    @computed public get freebetCreditsToalAmount(): Amount {
        try {
            return this.freebetCredits.type === 'ready'
                ? $appState.configState.precision.newFromOld(this.freebetCredits.value.totalAmount)
                : new Amount('0');
        } catch (error) {
            console.error(error);
            return new Amount('0');
        }
    }

    @action public handleShowModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        this.showModal = true;

        const value = event.currentTarget.getAttribute('data-value');

        const creditId = parseFloat(value ?? '');

        if (isNaN(creditId)) {
            return;
        }

        this.creditId = creditId;
    };

    @action public handleShowModalBonusEngine = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        this.showModal = true;
        const value = event.currentTarget.getAttribute('data-value');
        this.creditId = value;
    };

    @action public refreshResources = (): void => {
        this.freebetCreditsResource
            .refresh()
            .then(() => {})
            .catch(() => {});
        this.refreshFreebetLists();
    };

    @action public handleChangeReason = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.reason = event.currentTarget.value;
    };

    @action public resetState = (): void => {
        this.reason = '';
        this.showModal = false;
        this.creditId = null;
    };

    @action public handleRemoveFreebet = async (isAccept: boolean): Promise<void> => {
        if (this.creditId === null || isAccept === false) {
            this.resetState();
            return;
        }

        if (typeof this.creditId !== 'number') {
            return;
        }

        await this.sdkStaff.patchFreebetEnableCredit({
            credit_id: this.creditId,
            requestBody: {
                enabled: false,
                reason: this.reason,
            },
        });

        this.refreshResources();
        this.resetState();
        this.reduxAction.refreshFreebets(this.customerId);
    };

    @action public handleRemoveFreebetBonusEngine = async (isAccept: boolean): Promise<void> => {
        try {
            if (this.creditId === null || isAccept === false) {
                this.resetState();
                return;
            }

            if (typeof this.creditId !== 'string') {
                this.resetState();
                return;
            }

            await this.cancelBonusBonusEngine(this.creditId);
            this.resetState();
        } catch (e) {
            console.error(e);
            this.resetState();
        }
    };
}
